/**
 * Help Context
 *
 * Provides help content state management across the application
 */

import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { helpAPI } from '../../lib/api/help';
import type { PageHelp, FieldHelp, HelpSearchResult, HelpContextType } from './types';

const HelpContext = createContext<HelpContextType | undefined>(undefined);

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  // Page help state
  const [currentPageHelp, setCurrentPageHelp] = useState<PageHelp | null>(null);
  const [isPageHelpLoading, setIsPageHelpLoading] = useState(false);
  const [isPageHelpOpen, setIsPageHelpOpen] = useState(false);
  const currentPageKeyRef = useRef<string>('');

  // Field help cache
  const fieldHelpCacheRef = useRef<Map<string, FieldHelp>>(new Map());
  const [, forceUpdate] = useState({});

  // Glossary state
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  const [glossarySearchTerm, setGlossarySearchTerm] = useState('');

  // Set current page and fetch help
  const setCurrentPageKey = useCallback(async (pageKey: string) => {
    if (pageKey === currentPageKeyRef.current) return;

    currentPageKeyRef.current = pageKey;
    setIsPageHelpLoading(true);

    try {
      const response = await helpAPI.getPageHelp(pageKey);
      setCurrentPageHelp(response.data);
    } catch (error) {
      // Page help not found - that's OK, not all pages have help content
      setCurrentPageHelp(null);
    } finally {
      setIsPageHelpLoading(false);
    }
  }, []);

  // Page help actions
  const openPageHelp = useCallback(() => {
    setIsPageHelpOpen(true);
  }, []);

  const closePageHelp = useCallback(() => {
    setIsPageHelpOpen(false);
  }, []);

  // Field help methods
  const getFieldHelp = useCallback((fieldKey: string): FieldHelp | undefined => {
    return fieldHelpCacheRef.current.get(fieldKey);
  }, []);

  const fetchFieldHelp = useCallback(async (fieldKey: string): Promise<FieldHelp | null> => {
    // Check cache first
    if (fieldHelpCacheRef.current.has(fieldKey)) {
      return fieldHelpCacheRef.current.get(fieldKey) || null;
    }

    try {
      const response = await helpAPI.getFieldHelp(fieldKey);
      fieldHelpCacheRef.current.set(fieldKey, response.data);
      forceUpdate({});
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  const prefetchFieldHelp = useCallback(async (fieldKeys: string[]) => {
    // Filter out already cached keys
    const uncachedKeys = fieldKeys.filter(key => !fieldHelpCacheRef.current.has(key));
    if (uncachedKeys.length === 0) return;

    try {
      const response = await helpAPI.bulkGetFieldHelp(uncachedKeys);
      response.data.forEach(field => {
        fieldHelpCacheRef.current.set(field.field_key, field);
      });
      forceUpdate({});
    } catch (error) {
      // Silently fail - help content is not critical
    }
  }, []);

  // Glossary methods
  const openGlossary = useCallback((term?: string) => {
    setGlossarySearchTerm(term || '');
    setIsGlossaryOpen(true);
  }, []);

  const closeGlossary = useCallback(() => {
    setIsGlossaryOpen(false);
    setGlossarySearchTerm('');
  }, []);

  // Search
  const searchHelp = useCallback(async (query: string): Promise<HelpSearchResult[]> => {
    try {
      const response = await helpAPI.searchHelp(query);
      return response.data.results;
    } catch (error) {
      return [];
    }
  }, []);

  const value: HelpContextType = {
    currentPageHelp,
    isPageHelpLoading,
    isPageHelpOpen,
    openPageHelp,
    closePageHelp,
    setCurrentPageKey,
    fieldHelpCache: fieldHelpCacheRef.current,
    getFieldHelp,
    fetchFieldHelp,
    prefetchFieldHelp,
    isGlossaryOpen,
    glossarySearchTerm,
    openGlossary,
    closeGlossary,
    searchHelp,
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};
